import type { AbortUploadDetail } from 'shared/domain/messages/httpQueue/eventMessages';
import type { Message } from 'shared/domain/messages/message';
import { createSwSignal } from './swSignal';

/** A request the API answered with 401. */
export interface UnauthorizedResponse {
  url: string;
  method: string;
}

/** An event the worker published, with the key of its contract. */
export interface PublishedEvent {
  contractKey: string;
  event: Message;
}

/**
 * Facts the worker tells its own modules about. Each one is emitted in one
 * place; the modules that react subscribe in their `init`.
 */
export const swSignals = {
  /** The user signs out: running operations stop, state of the user is dropped. */
  loggedOut: createSwSignal('loggedOut'),
  /** Permissions to the selected project changed: its data is cleared, running operations stop. */
  permissionsChanged: createSwSignal('permissionsChanged'),
  /** Another project (or language) replaces the data of the selected project: running operations stop. */
  projectDataReplaced: createSwSignal('projectDataReplaced'),
  /** The app language changed: the project data is downloaded again. */
  languageChanged: createSwSignal('languageChanged'),
  /** The config (e.g. the selected project) was applied. */
  configApplied: createSwSignal('configApplied'),
  /** A table was flushed because the server rejected its sync key. */
  syncKeyInvalidated: createSwSignal('syncKeyInvalidated'),
  /** The API answered 422 (Unprocessable Entity): the stored project data is stale. */
  staleProjectDataDetected: createSwSignal('staleProjectDataDetected'),
  /** A request was added to the HTTP queue. */
  httpRequestQueued: createSwSignal('httpRequestQueued'),
  /** The page dropped a queued upload of a file. */
  uploadAborted: createSwSignal<AbortUploadDetail>('uploadAborted'),
  /** An entity is synced from the API: the permissions are refreshed along with it. */
  entitySyncRequested: createSwSignal('entitySyncRequested'),
  /** The API answered 401: the session ends and the user is signed out. */
  unauthorizedResponse: createSwSignal<UnauthorizedResponse>('unauthorizedResponse'),
  /** Something waits for the user's permissions to be stored: they are pulled now, without the cooldown of background syncs. */
  permissionsNeeded: createSwSignal('permissionsNeeded'),
  /** A pull of the permissions ended without storing them (no config or token, a failed request): what waits for them fails. */
  permissionsUnavailable: createSwSignal('permissionsUnavailable'),
  /** The user's permissions were stored: what waited to know the access to a project goes on. */
  permissionsStored: createSwSignal('permissionsStored'),
  /** The complete list of projects was stored and published: the selected project is published again. */
  projectListStored: createSwSignal('projectListStored'),
  /** The network is back (the worker's `online` event): what waited for it goes on. */
  networkRestored: createSwSignal('networkRestored'),
  /** The worker published an event of a contract (reads waiting for a download watch it). */
  eventPublished: createSwSignal<PublishedEvent>('eventPublished'),
};
